import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from "react";
import { Box, HStack, Text } from "native-base";
import { ProviderConnectionStatus, forHumans, logisticsForHumans, } from "@madmedical/medical";
import { useTranslation } from "@madmedical/i18n";
import { useRouteNavigate } from "../../providers/routing";
import { useCallCenterDevices } from "../../providers/callCenterDevice";
import Tag from "../../atoms/Tag";
import ReactTable from "../../molecules/ReactTable";
import Badge from "../../atoms/Badge";
import SearchInput from "../../molecules/Form/SearchInput";
import ActionButton from "../../molecules/ActionButton";
import CallCenterDeviceDesktopFilter from "../filters/desktop/CallCenterDeviceDesktopFilter";
const MAX_TAGS = 3;
const CallCenterDeviceList = ({ onEndReached, onEditPress }) => {
    const { devices, onSearchChange } = useCallCenterDevices();
    const [search, setSearch] = useState("");
    const navigate = useRouteNavigate();
    const { t } = useTranslation();
    const handleSearchChange = (search) => {
        setSearch(search);
        onSearchChange(search);
    };
    const handleRowPress = ({ id }) => {
        navigate("callcenter_device", { deviceId: id });
    };
    const columns = useMemo(() => [
        {
            Header: t("serialNumber"),
            accessor: "serialNumber",
            width: "10%",
        },
        {
            Header: t("device"),
            id: "deviceTypeName",
            width: "auto",
            Cell: ({ row: { original: { deviceType }, }, }) => _jsx(_Fragment, { children: deviceType.name }),
        },
        {
            Header: t("measures:type"),
            id: "metricTypes",
            width: "auto",
            Cell: ({ row: { original: { deviceType: { metricTypes }, }, }, }) => {
                const count = metricTypes.length;
                if (!count) {
                    return _jsx(Text, { children: "-" });
                }
                const types = metricTypes.slice(0, MAX_TAGS);
                const remains = count - types.length;
                return (_jsxs(HStack, Object.assign({ w: "full", flexWrap: "wrap" }, { children: [types.map((metricType) => (_jsx(Tag, Object.assign({ mr: 2, mb: 2 }, { children: t(`measures:${forHumans(metricType)}`) }), metricType))), remains ? (_jsxs(Tag, Object.assign({ mr: 2, mb: 2 }, { children: ["+", remains] }), remains)) : null] })));
            },
        },
        {
            Header: t("logistics"),
            accessor: "status",
            width: "12%",
            Cell: ({ value: status }) => (_jsx(Text, { children: logisticsForHumans(status) })),
        },
        {
            Header: t("connectionState"),
            accessor: "connectionStatus",
            width: "12%",
            Cell: ({ value: connectionStatus }) => {
                let badgeData = null;
                switch (connectionStatus) {
                    case ProviderConnectionStatus.Active:
                        badgeData = {
                            variant: "ghostSuccess",
                            text: t("active"),
                        };
                        break;
                    case ProviderConnectionStatus.Inactive:
                        badgeData = {
                            variant: "ghostError",
                            text: t("inactive"),
                        };
                        break;
                    default:
                        badgeData = {
                            variant: "ghost",
                            text: t("notConnected"),
                        };
                }
                return (_jsx(Badge, Object.assign({ mt: 2, size: "sm", variant: badgeData.variant }, { children: badgeData.text })));
            },
        },
        {
            Header: "",
            id: "actions",
            width: 40,
            Cell: ({ row }) => (_jsx(ActionButton, { actions: [
                    {
                        text: t("edit"),
                        onPress: () => onEditPress(row.original.id),
                    },
                ] })),
        },
    ], [onEditPress, t]);
    return (_jsxs(Box, Object.assign({ px: "6", py: "4", width: "full" }, { children: [_jsxs(HStack, Object.assign({ mb: "5", width: "full" }, { children: [_jsx(CallCenterDeviceDesktopFilter, {}), _jsx(SearchInput, { placeholder: t("search"), onChangeText: handleSearchChange, value: search })] })), _jsx(ReactTable, { columns: columns, data: devices, onEndReached: onEndReached, onPress: handleRowPress })] })));
};
export default CallCenterDeviceList;
